import React, { FC, ReactNode } from 'react';
import { usePrime } from 'clientState/providers/PrimeProvider';
import { useFeeType } from 'clientState/providers/FeeTypeProvider';
import {
  MEMBER_PRICE_POLICY_DISCOUNTED,
  CHEAPEST,
  MEMBER_PRICE_POLICY_UNDISCOUNTED,
} from '@components/Shared/FeeSelector/FeeTypeName/feeTypeConstantNames';
import { Body, Box, Flex } from '@edreamsodigeo/prisma-design-system';
import { MoneyComponent } from '../../../../../../Shared/Money';
import { createFeature, useTranslation } from '@edreamsodigeo/frontend-core';
import { ThreeColumnsItineraryType } from '../../../types';
import { getPrices } from '@components/Results/Components/Itinerary/ResultsItineraryPrice/ResultsItineraryDualPrice';
import PrimeMessage from '../../../PrimeMessage/PrimeMessage';
import PrimeLogo from '../../../../../../Prime/PrimeLogo';
import { PrimeDiscountPill } from '../../../PrimeDiscountPill/PrimeDiscountPill';
import { PRIME_DISCOUNT_VISIBILITY } from 'clientState/ab/aliases';
import { useIsAlternativeItineraryLayerOpen } from 'clientState/providers/AlternativeItineraryProvider/AlternativeItineraryLayerProvider';
import { useEqualProminencePrimeDisplayMobile } from 'clientState/hooks/useEqualProminencePrimeDisplayMobile';
import { usePersistedFlightSelection } from 'clientState/hooks/usePersistedFlightSelection/usePersistedFlightSelection';

const PrimeDiscountPillComponent = createFeature({
  name: PRIME_DISCOUNT_VISIBILITY,
  variations: [null, PrimeDiscountPill, PrimeDiscountPill],
  version: 1,
});

type PriceProps = {
  amount: number;
  currency: string;
  label: string;
  sx: Record<string, unknown> | undefined;
  sxLabel: Record<string, unknown> | undefined;
};

type SlashedPriceProps = {
  price: PriceProps;
  isLongCurrency: boolean;
};

const SlashedPrice: FC<SlashedPriceProps> = ({ price, isLongCurrency }) => {
  const sx = {
    '.money-integer': {
      color: 'neutral.70',
      textDecoration: 'line-through',
      fontSize: isLongCurrency ? '14px' : '16px',
      fontWeight: '400',
      display: 'inline-block',
    },
    '.money-currency': {
      textDecoration: 'line-through',
      fontSize: isLongCurrency ? '14px' : '16px',
      color: 'neutral.70',
      textAlign: 'right',
      fontWeight: '400',
      display: 'inline-block',
    },
  };

  return (
    <Box sx={{ marginBottom: '-4px', textAlign: 'right', color: 'neutral.70' }}>
      <MoneyComponent currency={price.currency} strikeThrough={'none'} sx={sx}>
        {price.amount}
      </MoneyComponent>
    </Box>
  );
};

type PriceLabelType = {
  shouldShowDualPrice: boolean;
  PriceDescription: ReactNode;
};

const StandardPriceLabel: React.FC<PriceLabelType> = ({
  shouldShowDualPrice,
}) => {
  const { t } = useTranslation();
  let priceLabel = t('prime.price.label');

  if (!shouldShowDualPrice) {
    priceLabel = t('results.price.label');
  }
  return <>{priceLabel}</>;
};

type ThreeColumnsItineraryPriceType = {
  itinerary: ThreeColumnsItineraryType;
  PriceDescription: ReactNode;
  hasRetailingTechniques?: boolean;
  isAlternativeItinerary?: boolean;
  isAlternativeItineraryOnModal?: boolean;
  isEqualProminencePrimeDisplayMobile?: boolean;
};

const ThreeColumnsItineraryPriceDefault: FC<ThreeColumnsItineraryPriceType> = ({
  itinerary,
  PriceDescription,
  hasRetailingTechniques = true,
  isAlternativeItinerary = false,
  isAlternativeItineraryOnModal = false,
  isEqualProminencePrimeDisplayMobile = false,
}) => {
  const { feeTypeSelected } = useIsAlternativeItineraryLayerOpen();
  const [flightSelectionData = {}] = usePersistedFlightSelection();
  const [feeType] = useFeeType();
  const { isUK, isPrimeTotalPriceOnCard, isPrimeSite, isLoggedPrimeUser } =
    usePrime() || {};
  const isDisplayPrimeDiscounts = isPrimeSite || isLoggedPrimeUser;
  const feeSelected = feeTypeSelected || feeType?.id;
  const isPrimePriceSelected =
    flightSelectionData?.data &&
    !flightSelectionData?.data?.priceWithoutDiscountSelected;

  const shouldShowDualPrice = isAlternativeItineraryOnModal
    ? (feeSelected && feeSelected !== MEMBER_PRICE_POLICY_UNDISCOUNTED) ||
      (feeType === null && isPrimePriceSelected) ||
      isAlternativeItinerary
    : isDisplayPrimeDiscounts &&
      [MEMBER_PRICE_POLICY_DISCOUNTED, CHEAPEST].includes(feeSelected);

  const { price, fees } = itinerary;
  const amount = price?.amount || 0;
  const isLongCurrency = amount > 999;
  const isVeryLongCurrency = amount > 999999;

  const currencyIconStyle = isLongCurrency
    ? {
        '.money-currency': {
          display: 'inline-block',
          textAlign: 'right',
          fontSize: isVeryLongCurrency ? '14px' : '18px',
        },
      }
    : {};

  const moneyStyle = {
    display: 'inline-block',
    textAlign: 'right',
    '.money-integer': {
      display: 'inline-block',
      textAlign: 'right',
      fontSize: isLongCurrency
        ? isVeryLongCurrency
          ? '14px'
          : '18px'
        : '24px',
    },
    ...currencyIconStyle,
  };

  let { primePrice } = getPrices(fees);
  const { noPrimePrice } = getPrices(fees);

  if (!shouldShowDualPrice && price) {
    primePrice = price;
  }

  let PriceSubtitle = StandardPriceLabel;

  if (
    feeSelected &&
    feeSelected !== MEMBER_PRICE_POLICY_UNDISCOUNTED &&
    !isLoggedPrimeUser &&
    (isUK || isPrimeTotalPriceOnCard || isEqualProminencePrimeDisplayMobile)
  ) {
    PriceSubtitle = PrimeMessage;
  }

  const priceToShow =
    feeSelected &&
    feeSelected === 'MEMBER_PRICE_POLICY_UNDISCOUNTED' &&
    isAlternativeItineraryOnModal &&
    !isAlternativeItinerary
      ? noPrimePrice
      : primePrice;

  return (
    <Flex flexDirection="column" alignItems={'flex-end'}>
      <Flex flexDirection="column" alignItems={'flex-end'}>
        {isUK && isLoggedPrimeUser && <PrimeLogo width="55px" />}
        {shouldShowDualPrice && (
          <SlashedPrice price={noPrimePrice} isLongCurrency={isLongCurrency} />
        )}
        <Flex flexDirection="column" alignItems={'flex-end'}>
          <Box marginX={isLongCurrency ? 'minus_2x_small' : null}>
            <MoneyComponent sx={moneyStyle} currency={priceToShow?.currency}>
              {priceToShow?.amount}
            </MoneyComponent>
          </Box>
          <Body
            color="neutral.70"
            type={'xxSmall'}
            weight="normal"
            textAlign={'right'}
            sx={{ lineHeight: '12px', whiteSpace: 'normal' }}
          >
            <PriceSubtitle
              shouldShowDualPrice={shouldShowDualPrice}
              PriceDescription={PriceDescription}
            />
          </Body>
          {shouldShowDualPrice && hasRetailingTechniques && (
            <PrimeDiscountPillComponent
              primePrice={primePrice}
              noPrimePrice={noPrimePrice}
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

const ThreeColumnsItineraryPriceWrapper = (
  props: ThreeColumnsItineraryPriceType
) => {
  const isEqualProminencePrimeDisplayMobile =
    useEqualProminencePrimeDisplayMobile();

  return (
    <ThreeColumnsItineraryPriceDefault
      {...props}
      isEqualProminencePrimeDisplayMobile={isEqualProminencePrimeDisplayMobile}
    />
  );
};

const ThreeColumnsItineraryPrice = createFeature({
  name: 'PRIME_PRIMO70M',
  variations: [
    ThreeColumnsItineraryPriceDefault,
    ThreeColumnsItineraryPriceWrapper,
  ],
  version: 1,
});

export default ThreeColumnsItineraryPrice;
